import { useMemo, useState } from "react";
import PortalStatusBadge from "./PortalStatusBadge";

const ROLE_OPTIONS = ["opdrachtgever", "beheerder", "lezer"];

export default function ClientPortalAccessTab({ portalAccess, onInvite, onActivate, onUpdate, onResendInvite, onBlock, onUnblock, onRevoke, loading }) {
  const summary = portalAccess?.summary || {};
  const portalUsers = Array.isArray(portalAccess?.portalUsers) ? portalAccess.portalUsers : [];
  const availableContacts = Array.isArray(portalAccess?.availableContacts) ? portalAccess.availableContacts : [];

  const [roles, setRoles] = useState({});

  const roleFor = (contactId, fallback = "opdrachtgever") => roles[contactId] || fallback;

  const hasUsers = portalUsers.length > 0;
  const hasAvailable = availableContacts.length > 0;

  const primaryLabel = useMemo(
    () => summary.primaryPortalContact?.naam || "Niet ingesteld",
    [summary.primaryPortalContact?.naam]
  );

  return (
    <section className="space-y-4">
      <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Algemene portaalstatus</h3>
          <PortalStatusBadge status={summary.status || "inactive"} />
        </div>
        <div className="grid gap-2 text-sm text-slate-600 md:grid-cols-2">
          <p><span className="text-slate-500">Portalgebruikers:</span> {summary.portalUsersCount || 0}</p>
          <p><span className="text-slate-500">Primair portalcontact:</span> {primaryLabel}</p>
          <p><span className="text-slate-500">Laatste login:</span> {summary.lastActivityAt || "-"}</p>
          <p><span className="text-slate-500">Open opdrachten zichtbaar:</span> {summary.openPortalAssignments || 0}</p>
        </div>
      </article>

      <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Contactpersonen met portaaltoegang</h3>

        {hasUsers ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="px-2 py-2">Naam</th>
                  <th className="px-2 py-2">E-mail</th>
                  <th className="px-2 py-2">Rol</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Laatste login</th>
                  <th className="px-2 py-2">Acties</th>
                </tr>
              </thead>
              <tbody>
                {portalUsers.map((user) => (
                  <tr key={user.contactId} className="border-b border-slate-100 align-top last:border-b-0">
                    <td className="px-2 py-2 text-slate-900">{user.naam}</td>
                    <td className="px-2 py-2 text-slate-600">{user.email || "-"}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <select
                          value={roleFor(user.contactId, user.role || "opdrachtgever")}
                          onChange={(event) => setRoles((prev) => ({ ...prev, [user.contactId]: event.target.value }))}
                          className="rounded border border-slate-200 px-2 py-1 text-xs"
                        >
                          {ROLE_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={loading}
                          onClick={() => onUpdate(user.contactId, { portalRole: roleFor(user.contactId, user.role || "opdrachtgever") })}
                          className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700"
                        >
                          Opslaan
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-2"><PortalStatusBadge status={user.status} /></td>
                    <td className="px-2 py-2 text-slate-600">{user.portalLastLoginAt || "-"}</td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        <button type="button" disabled={loading} onClick={() => onResendInvite(user.contactId)} className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700">Uitnodiging opnieuw</button>
                        {user.status === "blocked" ? (
                          <button type="button" disabled={loading} onClick={() => onUnblock(user.contactId)} className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700">Deblokkeren</button>
                        ) : (
                          <button type="button" disabled={loading} onClick={() => onBlock(user.contactId)} className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700">Blokkeren</button>
                        )}
                        <button type="button" disabled={loading} onClick={() => onRevoke(user.contactId)} className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700">Intrekken</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Nog geen portalgebruikers gekoppeld aan deze klant.</p>
        )}
      </article>

      <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Contactpersonen zonder portaaltoegang</h3>

        {hasAvailable ? (
          <div className="space-y-2">
            {availableContacts.map((contact) => (
              <div key={contact.contactId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">{contact.naam}</p>
                  <p className="text-xs text-slate-500">{contact.email || "Geen e-mail"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={roleFor(contact.contactId, "opdrachtgever")}
                    onChange={(event) => setRoles((prev) => ({ ...prev, [contact.contactId]: event.target.value }))}
                    className="rounded border border-slate-200 px-2 py-1 text-xs"
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <button type="button" disabled={loading} onClick={() => onInvite(contact.contactId, roleFor(contact.contactId, "opdrachtgever"))} className="rounded border border-slate-200 px-3 py-1.5 text-xs text-slate-700">Uitnodigen</button>
                  <button type="button" disabled={loading} onClick={() => onActivate(contact.contactId, roleFor(contact.contactId, "opdrachtgever"))} className="rounded bg-slate-900 px-3 py-1.5 text-xs text-white">Activeren</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            Alle contactpersonen hebben al portaaltoegang of er zijn nog geen contactpersonen toegevoegd.
          </div>
        )}
      </article>
    </section>
  );
}
